import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    try {
      Sentry.captureException(error, {
        extra: { componentStack: errorInfo.componentStack },
      });
    } catch {
      // Sentry may not be initialized — ignore
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // CST207: strings in this fallback are intentionally hardcoded in
      // German. The boundary may fire when i18next has not initialized or is
      // itself the source of the error, so calling t() here would be unsafe.
      // Any future multilingual fallback must still keep a static DE baseline.
      return (
        <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
          <Card className="w-full max-w-md shadow-card text-center">
            <CardHeader>
              <CardTitle className="text-xl">
                Ein unerwarteter Fehler ist aufgetreten
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Bitte laden Sie die Seite neu. Falls das Problem bestehen
                bleibt, kontaktieren Sie den Support.
              </p>
            </CardContent>
            <CardFooter className="justify-center">
              <Button onClick={this.handleReload}>Neu laden</Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
